<template>
  <div>
    <el-input
      v-model="content"
      placeholder="点击选择主诉"
      @focus="initData"
    />

    <el-dialog
      :visible.sync="dialogVisible"
      title="主诉选择"
      width="1000px"
      top="5vh"
      class="chief-complaint-dialog"
    >
      <!-- 已选关键词展示区 + 搜索 -->
      <div class="fixed-header">
        <div class="search-container">
          <el-input
            ref="searchInput"
            v-model="searchKeyword"
            placeholder="请输入关键词搜索 (Ctrl+F 聚焦)"
            prefix-icon="el-icon-search"
            clearable
            @input="handleSearch"
            @keydown.native="handleSearchKeydown"
          />
        </div>
        <div v-if="selectedItems.length > 0" class="selected-container">
          <div class="selected-label">已选中：</div>
          <div class="selected-items">
            <el-tag
              v-for="(item, idx) in selectedItems"
              :key="'selected-' + idx"
              closable
              @close="removeSelected(item.id)"
            >
              {{ item.name }}
            </el-tag>
          </div>
        </div>
      </div>

      <div class="chief-complaint-container">
        <!-- 左侧树形结构 -->
        <div
          ref="treeContainer"
          class="tree-container"
          tabindex="-1"
          :class="{ 'focus-area': focusArea === 'tree' }"
        >
          <el-tree
            ref="tree"
            :data="treeData"
            :props="treeProps"
            :expand-on-click-node="false"
            default-expand-all
            node-key="id"
            highlight-current
            @node-click="handleNodeClick"
          />
        </div>

        <!-- 右侧列表 -->
        <div
          ref="listContainer"
          class="list-container"
          tabindex="-1"
          :class="{ 'focus-area': focusArea === 'list' }"
        >
          <div v-loading="loading" class="keyword-con flex flex-row align-start items-wrap">
            <span
              v-for="(item, index) in filteredItems"
              :key="item.id"
              class="keyword cursor-pointer"
              :class="{ selected: isSelected(item), highlighted: index === highlightedIndex }"
              :style="keywordStyle"
              @click="toggleSelect(item)"
            >
              {{ item.name }}
              <i v-if="isSelected(item)" class="el-icon-check selected-icon" />
            </span>
            <div v-if="filteredItems.length === 0 && !loading" class="empty-text">
              暂无数据
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmSelection">确定（Ctrl+Enter）</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { postData } from "@/api/user/authApi"

export default {
  name: 'ChiefComplaint',
  props: {
    value: {
      type: String,
      default: ''
    },
    diagParams: {
      type: Object,
      default: () => ({
        code: '',
        diagDictionaryCode: '',
        pinyinCode: '',
        commonSign: '',
        mainSign: 1,
        chiefData: ''
      })
    }
  },
  data() {
    return {
      dialogVisible: false,
      content: this.value,
      treeData: [],
      allItems: [],
      filteredItems: [],
      selectedItems: [],
      loading: false,
      searchKeyword: '',
      treeProps: {
        label: 'name',
        children: 'children'
      },
      currentNode: null,
      keywordWidth: 116,
      // 键盘交互新增状态
      highlightedIndex: -1,
      focusArea: 'search',
      treeHighlightedKey: null,
      flatTreeNodes: [],
      cols: 1
    }
  },
  computed: {
    keywordStyle() {
      return {
        width: `${this.keywordWidth}px`,
        fontSize: '14px'
      }
    },
    storeCode() {
      return this.$store.getters['user/getStoreCode']
    },
    levelOneField() {
      return `symDirectoryLevelOneName`
    },
    levelTwoField() {
      return `symDirectoryLevelTwoName`
    },
    levelThreeField() {
      return `symDirectoryLevelThreeName`
    },
    dictionaryNameField() {
      return `symDictionaryName`
    },
    highlightedItem() {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredItems.length) {
        return this.filteredItems[this.highlightedIndex]
      }
      return null
    }
  },
  watch: {
    value(val) {
      this.content = val
    },
    diagParams: {
      deep: true,
      handler() {
        if (this.dialogVisible) {
          this.loadData()
        }
      }
    },
    dialogVisible(val) {
      if (val) {
        document.addEventListener('keydown', this.handleGlobalKeydown)
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
          this.focusArea = 'search'
        })
      } else {
        document.removeEventListener('keydown', this.handleGlobalKeydown)
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.calculateCols)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateCols)
    document.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  methods: {
    initData() {
      this.dialogVisible = true
      this.selectedItems = []
      this.searchKeyword = ''
      this.currentNode = null
      this.highlightedIndex = -1
      this.focusArea = 'search'
      this.treeHighlightedKey = null
      this.loadData()

      if (this.content) {
        const names = this.content.split(' ').filter(Boolean)
        setTimeout(() => {
          this.selectedItems = this.allItems.filter(item =>
            names.includes(item.name)
          )
        }, 600)
      }
    },
    loadData() {
      // 优先 chiefData（编码），如果没有则用 diagDictionaryCode（名称）兜底
      const dictionaryValue = this.diagParams?.chiefData || this.diagParams?.diagDictionaryCode

      if (!this.diagParams || !this.diagParams.code || !dictionaryValue) {
        this.$message.warning('请选择相关病症')
        this.dialogVisible = false
        return
      }

      this.loading = true
      const dynamicField = `${this.diagParams.code}DictionaryCode`
      const pd = {
        // 关键修改：接口现在支持用名称查询，且和code共用字段
        // 当 chiefData（编码）为空时，使用 diagDictionaryCode（名称）作为查询值
        [dynamicField]: this.diagParams.chiefData || this.diagParams.diagDictionaryCode,
        storeCode: this.storeCode,
        pinyinCode: this.diagParams.pinyinCode,
        commonSign: 1,
        mainSign: 1
      }

      const apiPath = `clinic/clinicdic/clinicdiagdic/clinicsymdic/find/findsymdictionaryby${this.diagParams.code.toLowerCase()}`
      postData(apiPath, pd)
        .then(res => {
          const rawData = res.data || []
          this.treeData = this.buildTree(rawData)
          this.allItems = this.extractAllDictionaryItems(rawData)
          this.filteredItems = [...this.allItems]
          this.flatTreeNodes = this.flattenTree(this.treeData)
          if (this.treeData.length > 0) {
            this.treeHighlightedKey = this.treeData[0].id
          }
          this.$nextTick(() => this.calculateCols())
        })
        .catch(err => {
          console.error('获取数据失败', err)
          this.$message.error('获取数据失败，请重试')
        })
        .finally(() => {
          this.loading = false
        })
    },

    buildTree(items) {
      const levelOneMap = new Map()
      const levelTwoMap = new Map()
      const levelThreeMap = new Map()

      items.forEach(item => {
        const levelOneName = item[this.levelOneField]
        const levelTwoName = item[this.levelTwoField]
        const levelThreeName = item[this.levelThreeField]

        if (!levelOneName) return

        if (!levelOneMap.has(levelOneName)) {
          levelOneMap.set(levelOneName, {
            id: `level1-${levelOneName}`,
            name: levelOneName,
            level: 1
          })
        }
        const levelOneNode = levelOneMap.get(levelOneName)

        if (!levelTwoName) return

        const levelTwoId = `level2-${levelOneName}-${levelTwoName}`
        let levelTwoNode = levelTwoMap.get(levelTwoId)
        if (!levelTwoNode) {
          levelTwoNode = {
            id: levelTwoId,
            name: levelTwoName,
            level: 2
          }
          levelTwoMap.set(levelTwoId, levelTwoNode)
          if (!levelOneNode.children) {
            levelOneNode.children = []
          }
          levelOneNode.children.push(levelTwoNode)
        }

        if (!levelThreeName) return

        const levelThreeId = `level3-${levelOneName}-${levelTwoName}-${levelThreeName}`
        if (!levelThreeMap.has(levelThreeId)) {
          const levelThreeNode = {
            id: levelThreeId,
            name: levelThreeName,
            level: 3,
            leaf: true
          }
          levelThreeMap.set(levelThreeId, levelThreeNode)
          if (!levelTwoNode.children) {
            levelTwoNode.children = []
          }
          levelTwoNode.children.push(levelThreeNode)
        }
      })

      return Array.from(levelOneMap.values())
    },

    extractAllDictionaryItems(items) {
      return items.map(item => ({
        id: item.id,
        name: item[this.dictionaryNameField],
        rawData: item
      }))
    },

    flattenTree(nodes, result = []) {
      if (!nodes) return result
      nodes.forEach(node => {
        result.push(node)
        if (node.children && node.children.length > 0) {
          this.flattenTree(node.children, result)
        }
      })
      return result
    },

    calculateCols() {
      this.$nextTick(() => {
        const container = this.$refs.listContainer
        const firstItem = container?.querySelector('.keyword')
        if (container && firstItem) {
          const containerWidth = container.clientWidth - 20
          const itemWidth = firstItem.offsetWidth + 8
          this.cols = Math.max(1, Math.floor(containerWidth / itemWidth))
        } else {
          this.cols = 1
        }
      })
    },

    handleSearch() {
      const keyword = this.searchKeyword.trim().toLowerCase()
      if (!keyword) {
        if (this.currentNode) {
          this.handleNodeClick(this.currentNode)
        } else {
          this.filteredItems = [...this.allItems]
        }
        this.highlightedIndex = -1
        return
      }
      this.filteredItems = this.allItems.filter(item =>
        item.name.toLowerCase().includes(keyword)
      )

      // 唯一结果自动选中并清空搜索框，方便继续输入下一个词
      if (this.filteredItems.length === 1) {
        const item = this.filteredItems[0]
        if (!this.isSelected(item)) {
          this.selectedItems.push(item)
        }
        this.searchKeyword = ''
        this.filteredItems = [...this.allItems]
        this.highlightedIndex = -1
        this.$nextTick(() => this.calculateCols())
      } else {
        this.highlightedIndex = this.filteredItems.length > 0 ? 0 : -1
        this.$nextTick(() => this.calculateCols())
      }
    },

    handleSearchKeydown(e) {
      if (e.key === 'Enter') {
        e.preventDefault()
        this.handleSearchEnter()
      }
    },

    handleSearchEnter() {
      // 清空搜索框方便下次输入，但不改变当前过滤结果
      this.searchKeyword = ''
      if (this.filteredItems.length > 0) {
        this.focusArea = 'list'
        this.highlightedIndex = 0
        this.$nextTick(() => {
          this.$refs.listContainer?.focus()
          const items = this.$refs.listContainer?.querySelectorAll('.keyword')
          items?.[0]?.scrollIntoView({ block: 'nearest' })
        })
      }
    },

    handleNodeClick(node) {
      this.currentNode = node
      this.treeHighlightedKey = node.id
      if (this.searchKeyword) {
        this.searchKeyword = ''
      }

      if (node.level === 1) {
        this.filteredItems = this.allItems.filter(item =>
          item.rawData[this.levelOneField] === node.name
        )
      } else if (node.level === 2) {
        this.filteredItems = this.allItems.filter(item =>
          item.rawData[this.levelTwoField] === node.name
        )
      } else if (node.level === 3) {
        this.filteredItems = this.allItems.filter(item =>
          item.rawData[this.levelThreeField] === node.name
        )
      }
      this.highlightedIndex = this.filteredItems.length > 0 ? 0 : -1
      this.$nextTick(() => this.calculateCols())
    },

    handleGlobalKeydown(e) {
      if (!this.dialogVisible) return

      // Ctrl+F 重新聚焦搜索框（阻止浏览器默认查找）
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault()
        this.focusArea = 'search'
        this.$nextTick(() => {
          const input = this.$refs.searchInput
          if (input) {
            input.focus()
            input.$refs.input?.select()
          }
        })
        return
      }

      // Ctrl+Z 撤销最后一个选中
      if (e.ctrlKey && e.key === 'z') {
        if (this.selectedItems.length > 0) {
          e.preventDefault()
          this.selectedItems.pop()
        }
        return
      }

      // Ctrl+Enter 确定选择
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault()
        this.confirmSelection()
        return
      }

      // Shift+左右切换焦点区域（树形 <-> 关键词列表）
      if (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault()
        if (e.key === 'ArrowLeft') {
          this.focusArea = 'tree'
          this.flatTreeNodes = this.flattenTree(this.treeData)
          if (!this.treeHighlightedKey && this.flatTreeNodes.length > 0) {
            this.treeHighlightedKey = this.flatTreeNodes[0].id
          }
          this.$refs.tree?.setCurrentKey(this.treeHighlightedKey)
          this.$nextTick(() => this.$refs.treeContainer?.focus())
        } else {
          this.focusArea = 'list'
          this.highlightedIndex = this.filteredItems.length > 0 ? 0 : -1
          this.$nextTick(() => {
            this.$refs.listContainer?.focus()
            const items = this.$refs.listContainer?.querySelectorAll('.keyword')
            items?.[0]?.scrollIntoView({ block: 'nearest' })
          })
        }
        return
      }

      // 关键词列表区域导航
      if (this.focusArea === 'list') {
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          e.preventDefault()
          const total = this.filteredItems.length
          if (!total) return
          let idx = this.highlightedIndex
          if (idx < 0) idx = 0

          if (e.key === 'ArrowUp') idx -= this.cols
          else if (e.key === 'ArrowDown') idx += this.cols
          else if (e.key === 'ArrowLeft') idx -= 1
          else if (e.key === 'ArrowRight') idx += 1

          if (idx < 0) idx = 0
          if (idx >= total) idx = total - 1
          this.highlightedIndex = idx

          this.$nextTick(() => {
            const items = this.$refs.listContainer?.querySelectorAll('.keyword')
            items?.[idx]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
          })
          return
        }

        if (e.key === ' ') {
          e.preventDefault()
          this.toggleSelect(this.highlightedItem)
          return
        }
      }

      // 树形区域导航
      if (this.focusArea === 'tree') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault()
          const flat = this.flatTreeNodes
          if (!flat.length) return
          let idx = flat.findIndex(n => n.id === this.treeHighlightedKey)
          if (idx === -1) idx = 0
          if (e.key === 'ArrowUp') idx--
          else idx++
          if (idx < 0) idx = flat.length - 1
          if (idx >= flat.length) idx = 0
          this.treeHighlightedKey = flat[idx].id
          this.$refs.tree?.setCurrentKey(this.treeHighlightedKey)
          return
        }

        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          const node = this.flatTreeNodes.find(n => n.id === this.treeHighlightedKey)
          if (node) this.handleNodeClick(node)
          return
        }
      }

      // 阻止普通 Enter 触发 dialog 默认确定按钮（焦点不在输入框时）
      if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey && !e.altKey) {
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault()
        }
      }
    },

    toggleSelect(item) {
      if (!item) return
      const index = this.selectedItems.findIndex(i => i.id === item.id)
      if (index > -1) {
        this.selectedItems.splice(index, 1)
      } else {
        this.selectedItems.push(item)
      }
    },

    removeSelected(id) {
      this.selectedItems = this.selectedItems.filter(item => item.id !== id)
    },

    isSelected(item) {
      return this.selectedItems.some(i => i.id === item.id)
    },

    confirmSelection() {
      this.content = this.selectedItems.map(item => item.name).join(' ')
      this.$emit('input', this.content)
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped lang="scss">
.fixed-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  padding: 12px 20px;
  margin: 0 -20px 15px;
  border-bottom: 1px solid #E5E6EB;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  max-height: 25vh;
  overflow-y: auto;
  flex-shrink: 0;
}

.search-container {
  margin-bottom: 12px;
}

.selected-container {
  display: flex;
  align-items: flex-start;

  .selected-label {
    font-weight: bold;
    color: #165DFF;
    min-width: 60px;
    margin-top: 4px;
    flex-shrink: 0;
  }

  .selected-items {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1;

    :deep(.el-tag) {
      height: auto;
      white-space: normal;
      word-break: break-all;
      line-height: 1.4;
      padding: 4px 8px;
    }
  }
}

.chief-complaint-container {
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 10px;
  overflow: hidden;

  .tree-container {
    width: 30%;
    border: 1px solid #e5e6eb;
    border-radius: 4px;
    overflow: auto;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;

    &.focus-area {
      border-color: #165DFF;
      box-shadow: 0 0 0 2px rgba(22, 93, 255, 0.15);
    }
  }

  .list-container {
    flex: 1;
    border: 1px solid #e5e6eb;
    border-radius: 4px;
    overflow: auto;
    padding: 10px;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;

    &.focus-area {
      border-color: #165DFF;
      box-shadow: 0 0 0 2px rgba(22, 93, 255, 0.15);
    }
  }
}

.keyword-con {
  padding: 10px 0;

  .keyword {
    position: relative;
    border-radius: 2px;
    font-family: PingFang SC, PingFang SC, serif;
    font-weight: normal;
    font-size: 14px;
    color: #000;
    line-height: 22px;
    text-align: center;
    margin: 4px;
    padding: 0 5px;
    transition: all 0.3s;

    &:hover {
      background: #f0f7ff;
      color: #165DFF;
    }

    &.selected {
      background: #f0f7ff;
      color: #165DFF;
      font-weight: bold;
    }

    &.highlighted {
      outline: 2px solid #ffa940;
      outline-offset: 2px;
      border-radius: 4px;
    }

    .selected-icon {
      position: absolute;
      top: -5px;
      right: -5px;
      color: #fff;
      background: #165DFF;
      border-radius: 50%;
      font-size: 10px;
      width: 14px;
      height: 14px;
      line-height: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .empty-text {
    width: 100%;
    text-align: center;
    color: #909399;
    padding: 20px 0;
    font-size: 14px;
  }
}

.chief-complaint-dialog :deep(.el-dialog__body) {
  padding: 15px 20px;
  position: relative;
  height: 60vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
